import { useState, useEffect } from 'react';
import api, { formatApiError } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Trash2, Mail, Clock } from 'lucide-react';
import { toast } from 'sonner';

export default function ShareListModal({ listId, onClose }) {
  const [email, setEmail] = useState('');
  const [permission, setPermission] = useState('read');
  const [sharingData, setSharingData] = useState({ shared_with: [], pending_invitations: [] });
  const [loading, setLoading] = useState(false);

  useEffect(() => { fetchSharing(); }, [listId]);

  const fetchSharing = async () => {
    try {
      const { data } = await api.get(`/lists/${listId}/sharing`);
      setSharingData(data);
    } catch {}
  };

  const handleShare = async () => {
    if (!email.trim()) return;
    setLoading(true);
    try {
      await api.post(`/lists/${listId}/share`, { email, permission });
      setEmail('');
      fetchSharing();
      toast.success('Invitation sendt');
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };

  const handleRemove = async (userId) => {
    try {
      await api.delete(`/lists/${listId}/share/${userId}`);
      fetchSharing();
      toast.success('Adgang fjernet');
    } catch { toast.error('Kunne ikke fjerne adgang'); }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="bg-white border-[#EAE6DF] max-w-lg">
        <DialogHeader><DialogTitle className="text-[#2F3E3B]">Del liste</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Input value={email} onChange={e => setEmail(e.target.value)} placeholder="Email på bruger"
              type="email" className="border-[#EAE6DF] font-sans" data-testid="share-email-input"
              onKeyDown={e => e.key === 'Enter' && handleShare()} />
            <div className="flex gap-2">
              <Select value={permission} onValueChange={setPermission}>
                <SelectTrigger className="flex-1 border-[#EAE6DF] font-sans" data-testid="share-permission-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white border-[#EAE6DF]">
                  <SelectItem value="read" className="font-sans">Læse</SelectItem>
                  <SelectItem value="write" className="font-sans">Skrive</SelectItem>
                </SelectContent>
              </Select>
              <Button onClick={handleShare} disabled={loading || !email.trim()}
                className="bg-[#7F9C82] hover:bg-[#658368] text-white font-sans" data-testid="share-submit-button">
                {loading ? '...' : 'Del'}
              </Button>
            </div>
          </div>
          {(sharingData.shared_with.length > 0 || sharingData.pending_invitations.length > 0) && (
            <div className="space-y-2">
              <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#5C706A] font-sans">Delt med</p>
              {sharingData.shared_with.map((s, i) => (
                <div key={i} className="flex items-center justify-between py-2 px-3 bg-[#F9F8F6] rounded-lg">
                  <div className="flex items-center gap-2">
                    <Mail className="w-3.5 h-3.5 text-[#5C706A]" />
                    <span className="font-sans text-sm text-[#2F3E3B]">{s.name || s.email}</span>
                    <Badge variant="outline" className="text-[10px] border-[#EAE6DF] text-[#5C706A]">
                      {s.permission === 'write' ? 'Skrive' : 'Læse'}
                    </Badge>
                  </div>
                  <button onClick={() => handleRemove(s.user_id)}
                    className="p-1 rounded hover:bg-red-50 text-[#C85547]" data-testid={`remove-share-${i}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
              {sharingData.pending_invitations.map((p, i) => (
                <div key={i} className="flex items-center justify-between py-2 px-3 bg-[#F9F8F6] rounded-lg opacity-60">
                  <div className="flex items-center gap-2">
                    <Clock className="w-3.5 h-3.5 text-[#C87965]" />
                    <span className="font-sans text-sm text-[#5C706A]">{p.invitee_email}</span>
                    <Badge variant="outline" className="text-[10px] border-[#C87965]/30 text-[#C87965]">Afventer</Badge>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="font-sans" data-testid="close-share-modal">Luk</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
